import { Component, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { EntityIconService } from '@core/services/entity-icon/entity-icon.service';
import { DistinctRef } from '@core/services/gbe-error-parser/entity-ref-labels';
import { ICONS } from '@core/services/icons/icons.service';

/** One reference row: the entity, plus the roles it holds beyond its section's. */
export interface FindingCardRef {
  ref: DistinctRef;
  alsoLabel: string | null;
  /** The dataspace's display name for `ref.entityType`, resolved by the outlet. */
  typeLabel: string;
}

export interface FindingCardSection {
  heading: string;
  refs: FindingCardRef[];
}

/**
 * Dumb card for one finding occurrence: the message, its payload fields, and the entities
 * it names grouped by role. Every string arrives translated — the card resolves nothing.
 */
@Component({
  selector: 'app-plan-finding-card',
  imports: [TranslatePipe],
  templateUrl: './plan-finding-card.component.html',
  styles: `
    :host {
      display: contents;
    }
  `
})
export class PlanFindingCardComponent {
  readonly findingTitle = input.required<string>();
  readonly code = input.required<string>();
  readonly body = input.required<string>();
  readonly sections = input<FindingCardSection[]>([]);

  readonly openReference = output<{ ref: DistinctRef; anchorEl: HTMLElement }>();
  /** Not `copy`: that is a native DOM event name, and a host binding would shadow it. */
  readonly copyRequested = output<boolean>();
  readonly dismiss = output<void>();

  protected readonly ICONS = ICONS;

  private readonly entityIcons = inject(EntityIconService);

  /**
   * The row names the entity by uid, so the icon is what tells a reader which kind of
   * entity it is — the same icon the popover it opens shows in its header.
   */
  protected entityIcon(entityType: string): string {
    return this.entityIcons.getEntityIcon(entityType);
  }

  protected onReferenceClick(ref: DistinctRef, event: MouseEvent): void {
    if (ref.uid === null) return;
    this.openReference.emit({ ref, anchorEl: event.currentTarget as HTMLElement });
  }
}
